import { type FC, useState, Fragment, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import serverVars from 'server-vars';
import { trackEcommerce, trackingConstants, eventNameConstants } from 'dibs-tracking';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { FormattedMessage } from 'dibs-react-intl';
import slugify from 'dibs-slugify/exports/slugify.ts';

import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile.ts';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider.ts';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { useDeviceType } from '../../nftHeader/hooks/useDeviceType.tsx';
import { usePageType } from '../../nftHeader/hooks/usePageType.tsx';
import { FEATURED } from '../../nftHeader/pageType.ts';

import styles from './styles/NftExhibitionTileGrid.scss';

import { type NftExhibitionTileGrid_viewer$key } from './__generated__/NftExhibitionTileGrid_viewer.graphql.ts';
import { type NftExhibitionTileGrid_cmsNftExhibitionPage$key } from './__generated__/NftExhibitionTileGrid_cmsNftExhibitionPage.graphql.ts';

// get original query variables for refetch
const variables = serverVars.get('relayVariables');

type Props = {
    viewer: NftExhibitionTileGrid_viewer$key | null | undefined;
    cmsNftExhibitionPage: NftExhibitionTileGrid_cmsNftExhibitionPage$key | null | undefined;
};

const TilePlaceholder: FC = () => (
    <li className={styles.gridItem}>
        <div className={styles.placeholderImage} />
        <div className={styles.placeholderDetails}>
            <div className={styles.placeholderCreator} />
            <div className={styles.placeholderTitle} />
        </div>
    </li>
);

const placeholderTiles = [...Array(variables?.itemCount || 0)].map((_, i) => (
    <TilePlaceholder key={`placeholder-${i}`} />
));

type TrackingData = {
    list: string;
    id: string | null;
    name?: string | null;
    brand?: string | null;
    index: number;
};

const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;
const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST } = eventNameConstants;

const track = (
    type: typeof ECOM_PRODUCT_CLICK | typeof ECOM_PRODUCT_IMPRESSION,
    { index, ...rest }: TrackingData
): void => {
    let eventName;
    switch (type) {
        case ECOM_PRODUCT_CLICK:
            eventName = EVENT_SELECT_ITEM;
            break;
        case ECOM_PRODUCT_IMPRESSION:
            eventName = EVENT_VIEW_ITEM_LIST;
            break;
    }
    trackEcommerce({
        type,
        eventName,
        category: '/nft/art',
        actionField: { itemId: rest.id, list: rest.list },
        products: [{ category: '/nft/art', variant: 'NFT', position: index + 1, ...rest }],
    });
};

export const NftExhibitionTileGrid: FC<Props> = ({
    viewer: viewerRef,
    cmsNftExhibitionPage: cmsNftExhibitionPageRef,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const { isMobile } = useDeviceType();
    const pageType = usePageType();
    const isFeatured = pageType === FEATURED;
    const { currency } = useCurrencyStore();

    const list = `${pageType} module`;

    const viewer = useFragment(
        graphql`
            fragment NftExhibitionTileGrid_viewer on Viewer {
                ...NftHeartPillProvider_viewer
                ...NftItemTile_viewer
            }
        `,
        viewerRef
    );

    const cmsNftExhibitionPage = useFragment(
        graphql`
            fragment NftExhibitionTileGrid_cmsNftExhibitionPage on NftCmsExhibitionPage {
                urlLabel
                featuredItemsConnection(first: $first) {
                    edges {
                        node {
                            displayRow
                            displayOrder
                            nftItem {
                                serviceId
                                creatorAddress {
                                    publicProfile {
                                        profileName
                                    }
                                }
                                metadata {
                                    ... on NftDigitalArtMetadata {
                                        name
                                    }
                                }
                                ...NftItemTile_nftItem
                                mostRecentAuction {
                                    ...NftItemTile_nftAuction
                                }
                            }
                        }
                    }
                }
            }
        `,
        cmsNftExhibitionPageRef
    );

    const [itemsAndActionsTiles, itemIds] = useMemo(() => {
        const tiles = [];
        const ids = [];
        const results = cmsNftExhibitionPage?.featuredItemsConnection?.edges || [];
        const Wrapper = isFeatured ? Fragment : 'li';
        let displayRow = results?.[0]?.node?.displayRow;
        for (let index = 0; index < results.length; index++) {
            const { nftItem, displayOrder } = results[index]?.node || {};
            const prevRow = displayRow;
            displayRow = results[index]?.node?.displayRow;
            if (!nftItem?.serviceId || !viewer) {
                continue; // skip iteration
            }
            ids.push(nftItem.serviceId);
            const tracking = {
                list,
                index,
                id: nftItem.serviceId,
                name: nftItem.metadata?.name,
                brand: nftItem.creatorAddress?.publicProfile?.profileName,
            };
            if (prevRow !== displayRow) {
                // insert separator when displayRow changes
                tiles.push(
                    <Wrapper
                        key={'rule' + displayRow}
                        role="separator"
                        className={styles.horizontalRule}
                    />
                );
            }
            tiles.push(
                <Wrapper className={styles.gridItem} key={nftItem.serviceId + displayOrder}>
                    <NftItemTile
                        tileType="REGULAR_TILE"
                        gaLabel="NFT-Marketplace"
                        isMobile={isMobile}
                        nftAuction={nftItem.mostRecentAuction}
                        nftItem={nftItem}
                        viewer={viewer}
                        currency={currency}
                        fireImpressionTracking={() => track(ECOM_PRODUCT_IMPRESSION, tracking)}
                        fireTracking={() => track(ECOM_PRODUCT_CLICK, tracking)}
                    />
                </Wrapper>
            );
        }
        return [tiles, ids];
    }, [cmsNftExhibitionPage, currency, isFeatured, isMobile, list, viewer]);

    return (
        <NftHeartPillProvider viewer={viewer} nftItemIds={itemIds}>
            <div className={styles.container}>
                {isFeatured && isMobile ? (
                    <Swiper
                        itemsPerPage={1.25}
                        onPageChange={(index: number) => setCurrentIndex(index)}
                        startingPage={currentIndex}
                        classNames={{
                            wrapper: styles.swiperCarousel,
                            item: styles.listItem,
                        }}
                    >
                        {itemsAndActionsTiles}
                    </Swiper>
                ) : (
                    <ul className={styles.grid}>{itemsAndActionsTiles || placeholderTiles}</ul>
                )}
            </div>
            {isFeatured && (
                <div className={styles.viewMoreContainer}>
                    <Button
                        type="secondary"
                        size="medium"
                        href={`/nft-collections/exhibitions/${slugify(
                            cmsNftExhibitionPage?.urlLabel
                        )}`}
                        className={styles.viewMore}
                    >
                        <FormattedMessage
                            id="nft.exhibition.featured.viewMore"
                            defaultMessage="View More"
                        />
                    </Button>
                </div>
            )}
        </NftHeartPillProvider>
    );
};
